<template>
  <tab-layout :full-width="true">
    <div
      slot="tabContent"
      :style="appearances.schema_color 
        ? { 
          '--primary': appearances.schema_color,  
          '--primary-dark': appearances.schema_color,
          '--primary-rgb': appearances.schema_color,
        } : null"
    >

      <div class="leaderboard nominate bg-white rounded mb-4">


        <div class="p-3">
          <my-drafts-summary :appearances="appearances" @has-items="hasMyDrafts = $event" />

          <page-heading>Upcoming and Other Drafts</page-heading>
          <appearances-drafts-header v-if="!hasMyDrafts" :appearances="appearances" />
        </div>

        <div class="zone-filter">
          <div class="zone-filter__inner">
            <label class="form-label">Zone</label>
            <select class="form-control" :value="zoneId" @change="handleZoneChange($event.target.value)">
              <option value="">All</option>
              <option v-for="zone in zones" :key="zone.id" :value="zone.id">{{ zone.code }}</option>
            </select>
          </div>
        </div>

        <div class="position-relative">
          <div v-if="loading" class="table-overlay">
            <div class="table-spinner" />
          </div>

          <div class="__container nominate__item _type-other-drafts p-3">
            <appearances-drafts-desktop-banner :appearances="appearances" />

            <p v-if="!loading && items.length === 0" class="text-muted">No drafts available</p>

            <div v-for="draft in items" :key="draft.id" class="draft">
              <div class="nominate__item-wrap-name">{{ draft.name }}</div>
              <div class="mb-2">Starts: {{ formatDate(draft.start_date) }}</div>

              <template v-if="getDraftButton(draft)">
                <button
                  v-if="getDraftButton(draft).variant === 'disabled'"
                  class="nominate__click btn btn-secondary btn-sm btn-block"
                  disabled
                >
                  {{ getDraftButton(draft).text }}
                </button>
                <router-link
                  v-else-if="getDraftButton(draft).variant === 'outline'"
                  :to="{ name: 'Nominations Add', params: { draftId: String(draft.id) } }"
                  class="btn btn-outline-primary btn-sm btn-block"
                >
                  {{ getDraftButton(draft).text }}
                </router-link>
                <router-link
                  v-else
                  :to="{ name: 'Nominations Add', params: { draftId: String(draft.id) } }"
                  class="btn btn-primary btn-sm btn-block"
                >
                  {{ getDraftButton(draft).text }}
                </router-link>
              </template>
            </div>
          </div>
        </div>

        <div class="footer">
          <transition name="banner-fade">
            <div v-if="appearances.sponsor_logo_mobile && !bannerHidden" class="banner">
              <img :src="appearances.sponsor_logo_mobile" />
            </div>
          </transition>
        </div>
      </div>

      <pagination
        v-if="lastPage > 1"
        :page="currentPage"
        :total-pages="lastPage"
        :on-change="handlePageChange"
        :color="appearances.schema_color"
        class="paginate-leaderboard"
      />
    </div>
  </tab-layout>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted, onUnmounted, getCurrentInstance } from '@vue/composition-api'
import TabLayout from '@/components/TabLayout'
import PageHeading from '@/components/PageHeading'
import Pagination from '@/components/Pagination'
import AppearancesDraftsHeader from '@/views/Leaders/Listing/components/AppearancesDraftsHeader'
import AppearancesDraftsDesktopBanner from '@/views/Leaders/Listing/components/AppearancesDraftsDesktopBanner'
import MyDraftsSummary from '@/views/Nominations/components/MyDraftsSummary'
import useAppearance from '@/composables/appearances/useAppearance'
import useZones from '@/composables/useZones'
import useNominationsList from '@/composables/nominations/useNominationsList'
import { formatLongDate, formatLongDateTime } from '@/utils/date'

function formatDate(str) {
  if (!str) return '—'
  return formatLongDate(str)
}

function getDraftButton(draft) {
  if (draft.state == null) return null
  const now = new Date()
  if (draft.state.id === 1) {
    const nomsOpen = new Date(draft.noms_open.replace(' ', 'T'))
    const nomsClosed = new Date(draft.noms_closed.replace(' ', 'T'))
    if (nomsOpen > now) return { text: `Opens ${formatLongDateTime(draft.noms_open)}`, variant: 'disabled' }
    if (nomsClosed < now) return { text: 'Draft is being finalised', variant: 'outline' }
    return { text: 'Click here to nominate', variant: 'primary' }
  }
  if (draft.state.id === 5) return { text: 'Wait list is operating', variant: 'primary' }
  return null
}

export default defineComponent({
  name: 'NominationsDraftsList',
  components: {
    TabLayout,
    PageHeading,
    Pagination,
    AppearancesDraftsHeader,
    AppearancesDraftsDesktopBanner,
    MyDraftsSummary
  },
  setup() {
    const { proxy } = getCurrentInstance()
    const { form, previews } = useAppearance('nominations')
    const { zones } = useZones()
    const { items, loading, zoneId, currentPage, lastPage, fetch, setZone, setPage } = useNominationsList()

    const appearances = computed(() => ({ ...previews, ...form }))

    const hasMyDrafts = ref(false)
    const bannerHidden = ref(false)
    function onScroll() {
      if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 10) {
        bannerHidden.value = true
      }
    }
    onMounted(() => window.addEventListener('scroll', onScroll))
    onUnmounted(() => window.removeEventListener('scroll', onScroll))

    watch(
      () => proxy.$route.query,
      (query) => {
        setZone(query.zone_id != null ? query.zone_id : null)
        setPage(query.page != null ? Number(query.page) : 1)
        fetch()
      },
      { immediate: true }
    )

    function handleZoneChange(val) {
      const query = {}
      if (val !== '') query.zone_id = String(val)
      proxy.$router.replace({ query }).catch(() => {})
    }

    function handlePageChange(page) {
      const query = { ...proxy.$route.query }
      if (page > 1) {
        query.page = String(page)
      } else {
        delete query.page
      }
      proxy.$router.replace({ query }).catch(() => {})
    }

    return {
      appearances,
      hasMyDrafts,
      bannerHidden,
      zones,
      items,
      loading,
      zoneId,
      currentPage,
      lastPage,
      handleZoneChange,
      handlePageChange,
      formatDate,
      getDraftButton
    }
  }
})
</script>

<style scoped>
.banner-fade-enter-active,
.banner-fade-leave-active {
  transition: opacity 1s;
}

.banner-fade-enter,
.banner-fade-leave-to {
  opacity: 0;
}

.zone-filter {
  background-color: var(--gray-50);
  padding: 28px 32px;
  margin: 0 16px 16px;
  border-radius: 4px;
}

.zone-filter__inner {
  max-width: 160px;
}

.zone-filter__inner .form-label {
  margin-bottom: 4px;
  font-size: 14px;
}


</style>

<style>
.leaderboard.nominate h1 {
  color: var(--primary) !important;
}

.nominate__click:disabled {
  pointer-events: none;
}
</style>



// WEBPACK FOOTER //
// src/views/Nominations/DraftsList.vue